import type { CommentCountNodeArgs } from "@framework/comments/comments.types";
import {
  toCycloneCountId,
  type CycloneCommentIdentity,
} from "@framework/comments/cyclone-comment-identity";
import type { Game } from "@framework/in5/types";
import { getCoralIdFromNode } from "@voxmedia/duet-utils";

export type In5GameCommentIdentity = Pick<Game, "uuid" | "chorus_entry_id">;

/**
 * Adapts an in-5 game to {@link CommentCountNodeArgs} for shared comment-count
 * resolution (sticky nav, Coral id lookup, etc.).
 *
 * Cyclone uses `nodeId` (game guid); Coral legacy prefers `chorusId` when present.
 */
export function mapIn5GameToCommentCountNodeArgs(
  game: In5GameCommentIdentity,
): CommentCountNodeArgs {
  const chorusEntryId = game.chorus_entry_id?.trim();
  const parsedChorusId = chorusEntryId
    ? parseInt(chorusEntryId, 10)
    : Number.NaN;

  return {
    nodeId: game.uuid,
    chorusId: Number.isNaN(parsedChorusId) ? null : parsedChorusId,
  };
}

/** Cyclone comment identity for an in-5 game (Firestore game guid). */
export function getIn5CycloneCommentIdentity(
  game: Pick<Game, "uuid">,
): CycloneCommentIdentity {
  return {
    streamId: game.uuid,
    contentSource: "in5",
  };
}

/** IndexedDB count key for cyclone in-5 comments (Honeypot support pending). */
export function getIn5CycloneCommentCountId(game: Pick<Game, "uuid">): string {
  return toCycloneCountId(getIn5CycloneCommentIdentity(game));
}

/** Coral story id for an in-5 game (legacy chorus id or game guid). */
export function getIn5CoralCommentId(game: In5GameCommentIdentity): string {
  return getCoralIdFromNode(mapIn5GameToCommentCountNodeArgs(game));
}
